import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { mdmApi, mdmKeys, type ClusterFilters } from '@/features/mdm/api';
import type { ResolveDatasetInput } from '@/features/mdm/types';

export function useClusters(filters: ClusterFilters) {
  return useQuery({
    queryKey: mdmKeys.clusters(filters),
    queryFn: () => mdmApi.clusters(filters),
  });
}

function useInvalidateClusters() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['mdm', 'clusters'] });
}

export function useResolveDataset() {
  const invalidate = useInvalidateClusters();
  return useMutation({
    mutationFn: ({ datasetId, input }: { datasetId: string; input: ResolveDatasetInput }) =>
      mdmApi.resolveDataset(datasetId, input),
    onSuccess: invalidate,
  });
}

export function useUpdateGolden() {
  const invalidate = useInvalidateClusters();
  return useMutation({
    mutationFn: ({ id, golden }: { id: string; golden: Record<string, unknown> }) =>
      mdmApi.updateGolden(id, golden),
    onSuccess: invalidate,
  });
}

export function useReviewCluster() {
  const invalidate = useInvalidateClusters();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      action === 'approve' ? mdmApi.approve(id) : mdmApi.reject(id),
    onSuccess: invalidate,
  });
}

export function useClearClusters() {
  const invalidate = useInvalidateClusters();
  return useMutation({
    mutationFn: (status?: string) => mdmApi.clearAll(status),
    onSuccess: invalidate,
  });
}
